<script setup>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Button} from "primevue";
import {defineProps, defineEmits, ref, provide, computed} from 'vue'
import {gregorianDate} from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import UpdateHotOrder from "@/views/orders/Details/hotOrders/UpdateHotOrder.vue";
import AddHotOrder from "@/views/orders/Details/hotOrders/AddHotOrder.vue";
import HotOrderLog from "@/views/orders/Details/hotOrders/HotOrderLog.vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {useUserStore} from "@/stores/user.js";
import DeliveredRating from "@/views/orders/Details/hotOrders/DeliveredRating.vue";
import CanceledRating from "@/views/orders/Details/hotOrders/CanceledRating.vue";
import AddUpdateDog from "@/views/orders/Details/hotOrders/AddUpdateDog.vue";
import UpdateUpdateDog from "@/views/orders/Details/hotOrders/UpdateUpdateDog.vue";
import {showMessage} from "@/utils/showMessage.js";

const props = defineProps({
  items: {
    type: Array,
    default: []
  },
  order: {
    type: Object,
    required: true
  },
  orderType: {
    type: String,
    required: true
  },
  addOrder: {
    type: Function,
    required: false
  },
  validateForm: {
    type: Function,
    required: true
  }
})
const emit = defineEmits(['refresh', 'refreshOrder'])

const ordersStore = useMainOrdersStore()
const userStore = useUserStore();
const confirm = useConfirm();
const selectedHotOrder = ref(null)
const toast = useToast();
const isShowUpdateHotOrderModal = ref(false)
const isShowAddHotOrderModal = ref(false)
const isShowHotOrderLog = ref(false)
const isShowDeliveredRatingModal = ref(false);
const isShowAddUpdateDogModal = ref(false);
const isShowUpdateUpdateDogModal = ref(false);
const isShowCanceledRatingModal = ref(false);
const hotOrders = ref(props.items || [])
const hasUpdatedog = computed(() => {

})

function formatNumber(num, decimals = 8) {
  return num.toFixed(decimals).replace(/\.?0+$/, '');
}

function showHotOrderLog(data) {
  isShowHotOrderLog.value = true
  selectedHotOrder.value = data
}

function showUpdateUpdateDogModal(data) {
  isShowUpdateUpdateDogModal.value = true
  selectedHotOrder.value = data
}

function showAddUpdateDogModal(data) {
  isShowAddUpdateDogModal.value = true
  selectedHotOrder.value = data
}

function closeHotOrderLog() {
  selectedHotOrder.value = {}
  isShowHotOrderLog.value = false
}

function showAddHotOrderModal() {
  const isValid = props.validateForm()
  if (isValid) {
    isShowAddHotOrderModal.value = true
  }
}

function showUpdateHotOrderModal({data}) {
  selectedHotOrder.value = data
  isShowUpdateHotOrderModal.value = true
}

async function removeHotOrder(data) {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are deleting sub order? (${data.title})`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Delete',
      size: 'small'
    },
    accept: async () => {
      try {
        const res = await ordersStore.action_removeHotOrder(props.orderType, {id: data.id})
        if (res.result === true) {
          //toast.add({severity: 'success', summary: 'Successful', detail: 'Taker Deleted', life: 3000});
          showMessage('Taker Deleted', 'success');
          //update order after remove hotOrder
          /*const res2 = await ordersStore.updateOrder(props.orderType, {
            id: props.order.id,
            takersCut: formatNumber(Number(props.order.takersCut) - Number(data.value)),
            revenue: formatNumber(Number(props.order.revenue) + Number(data.value))
          })

          if (res2 === true) {
            toast.add({severity: 'success', summary: 'Successful', detail: `Order Updated!`, life: 1500});
          } else {
            toast.add({severity: 'error', summary: 'Failed', detail: res2, life: 1500});
          }*/

          emit('refreshOrder')
          //await getHotOrders();
        } else {
          //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 3000});
          showMessage(res, 'error');
        }
      } catch (err) {
        //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 3000});
        showMessage(err, 'error');
      } finally {
      }
    },
    reject: () => {
      return
    }
  });
}

const getHotOrders = async () => {
  const res = await ordersStore.action_getHotOrders(props.orderType, {orderID: props.order.id})
  if (res.result === true) {
    hotOrders.value = res.data
  } else {
    //toast.add({severity: 'error', summary: 'Getting Takers Failed', detail: res, life: 1500});
    showMessage(res, 'error');
  }
}

const showRatingModal = (hotOrder) => {
  if (hotOrder.state === 'Canceled') {
    selectedHotOrder.value = hotOrder
    isShowCanceledRatingModal.value = true;
  } else {
    selectedHotOrder.value = hotOrder
    isShowDeliveredRatingModal.value = true;
  }
}

provide('isShowAddHotOrderModal', isShowAddHotOrderModal)
provide('isShowUpdateHotOrderModal', isShowUpdateHotOrderModal)
provide('isShowHotOrderLog', isShowHotOrderLog)
provide('isShowDeliveredRatingModal', isShowDeliveredRatingModal)
provide('isShowCanceledRatingModal', isShowCanceledRatingModal)
provide('isShowAddUpdateDogModal', isShowAddUpdateDogModal)
provide('isShowUpdateUpdateDogModal', isShowUpdateUpdateDogModal)
</script>

<template>
  <div class="flex-1 flex flex-col gap-4 max-h-full min-h-[200px] w-full">
    <div class="flex-1 col-span-12 flex flex-col gap-1 text-xs 2xl:text-sm h-full w-full">
      <div class="flex flex-row justify-between items-end">
        <div class="flex flex-row gap-4">
          <label for="takers">Sub Orders:</label>
          <span>{{ hotOrders.length }}</span>
        </div>
        <button class="py-1 px-2 bg-slate-800 text-xs" @click="showAddHotOrderModal">Add</button>
      </div>
      <div
          class="flex-1 w-full overflow-hidden border border-slate-700 rounded-md flex flex-col">
        <div class="overflow-y-auto custom-scrollbar flex flex-col gap-2 h-full w-full">
          <DataTable
              :nullSortOrder="-1"
              :value="hotOrders"
              scrollable
              scrollHeight="flex"
              :virtualScrollerOptions="{ itemSize: 30, class:'w-full' }"
              resizableColumns
              removableSort
              stripedRows
              class="text-xs w-full"
              size="small"
              tableStyle="width: 100%; table-layout: fixed;"
              row-hover
              @row-click="showUpdateHotOrderModal"
              :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
          >
            <Column field="uuid" header="#" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 3%; min-width: 50px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <span @click.stop class="w-full block whitespace-nowrap text-ellipsis overflow-hidden">
                  {{ (slotProps.data.uuid).split("-")[1] }}
                </span>
              </template>
            </Column>
            <Column field="title" header="Title" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 12%; min-width: 120px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
            </Column>
            <Column field="description" header="Description" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 10%; min-width: 100px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
            </Column>
            <Column field="takerName" header="Taker" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap"
                    style="width: 8%; min-width: 70px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <span @click.stop class="w-full block whitespace-nowrap text-ellipsis overflow-hidden">
                  {{ slotProps.data.takerName }}
                </span>
              </template>
            </Column>
            <Column field="currency" header="Currency" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 6%; min-width: 65px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
            </Column>
            <Column field="amount" header="Amount" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 6%; min-width: 65px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
            </Column>
            <Column field="value" header="Value" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 6%; min-width: 65px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
            </Column>
            <Column field="state" header="Status" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 6%; min-width: 75px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <StateColumn :state="slotProps.data.state"/>
              </template>
            </Column>
            <Column v-if="orderType === 'Boost'" field="rating" header="Rating" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer !p-0 hover:text-yellow-400"
                    style="width: 8%; min-width: 75px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <div @click.stop="showRatingModal(slotProps.data)" v-if="slotProps.data.rating"
                     class="flex flex-row items-center gap-0.5 h-full w-full px-[0.5rem] py-[0.375rem]">
                  <span class="flex flex-row items-end">
                    {{ slotProps.data.rating }}
                  </span>⭐
                </div>
                <div v-else></div>
              </template>
            </Column>
            <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                    style="width: 6%; min-width: 105px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <span class="block overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer">
                  {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
                </span>
              </template>
            </Column>
            <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                    style="width: 7%; min-width: 100px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder"/>
              </template>
              <template #body="slotProps">
                <span class="block overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer">
                  {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
                </span>
              </template>
            </Column>
            <Column :exportable="false"
                    style="width: 7%; min-width: 80px; height: 30px">
              <template #body="slotProps">
                <div class="flex flex-row gap-2 justify-end items-center w-full">
                  <template v-if="orderType === 'Boost' && ['Assigning', 'Assigned', 'Delivering'].includes(slotProps.data.state)">
                    <template v-if="slotProps.data.updatedog">
                      <button v-if="['New', 'Pending'].includes(slotProps.data.updatedog.state)"
                              @click.stop="showUpdateUpdateDogModal(slotProps.data)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="M12 1c3.681 0 7 2.565 7 6v4.539c0 .642.189 1.269.545 1.803l2.2 3.298A1.517 1.517 0 0 1 20.482 19H15.5a3.5 3.5 0 1 1-7 0H3.519a1.518 1.518 0 0 1-1.265-2.359l2.2-3.299A3.25 3.25 0 0 0 5 11.539V7c0-3.435 3.318-6 7-6M6.5 7v4.539a4.75 4.75 0 0 1-.797 2.635l-2.2 3.298l-.003.01l.001.007l.004.006l.006.004l.007.001h16.964l.007-.001l.006-.004l.004-.006l.001-.006l-.003-.01l-2.199-3.299a4.75 4.75 0 0 1-.798-2.635V7c0-2.364-2.383-4.5-5.5-4.5S6.5 4.636 6.5 7M14 19h-4a2 2 0 1 0 4 0"/>
                        </svg>
                      </button>

                      <button v-else @click.stop="showUpdateUpdateDogModal(slotProps.data)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="M1.22 1.22a.75.75 0 0 1 1.06 0l20.5 20.5a.749.749 0 0 1-.326 1.275a.75.75 0 0 1-.734-.215L17.94 19H15.5a3.5 3.5 0 1 1-7 0H3.518a1.516 1.516 0 0 1-1.263-2.36l2.2-3.298A3.25 3.25 0 0 0 5 11.539V7q0-.441.073-.866L1.22 2.28a.75.75 0 0 1 0-1.06M6.5 7.56h-.001v3.979a4.75 4.75 0 0 1-.797 2.635l-2.2 3.298l-.003.01l.001.007l.004.006l.006.004l.007.001H16.44ZM10 19a2 2 0 1 0 4 0Zm2-16.5c-1.463 0-2.8.485-3.788 1.257l-.04.032a.75.75 0 1 1-.935-1.173l.05-.04C8.548 1.59 10.212 1 12 1c3.681 0 7 2.565 7 6v4.539c0 .642.19 1.269.546 1.803l1.328 1.992a.75.75 0 1 1-1.248.832l-1.328-1.992a4.75 4.75 0 0 1-.798-2.635V7c0-2.364-2.383-4.5-5.5-4.5"/>
                        </svg>
                      </button>
                    </template>

                    <template v-else>
                      <button @click.stop="showAddUpdateDogModal(slotProps.data)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path fill="currentColor"
                                d="M1.22 1.22a.75.75 0 0 1 1.06 0l20.5 20.5a.749.749 0 0 1-.326 1.275a.75.75 0 0 1-.734-.215L17.94 19H15.5a3.5 3.5 0 1 1-7 0H3.518a1.516 1.516 0 0 1-1.263-2.36l2.2-3.298A3.25 3.25 0 0 0 5 11.539V7q0-.441.073-.866L1.22 2.28a.75.75 0 0 1 0-1.06M6.5 7.56h-.001v3.979a4.75 4.75 0 0 1-.797 2.635l-2.2 3.298l-.003.01l.001.007l.004.006l.006.004l.007.001H16.44ZM10 19a2 2 0 1 0 4 0Zm2-16.5c-1.463 0-2.8.485-3.788 1.257l-.04.032a.75.75 0 1 1-.935-1.173l.05-.04C8.548 1.59 10.212 1 12 1c3.681 0 7 2.565 7 6v4.539c0 .642.19 1.269.546 1.803l1.328 1.992a.75.75 0 1 1-1.248.832l-1.328-1.992a4.75 4.75 0 0 1-.798-2.635V7c0-2.364-2.383-4.5-5.5-4.5"/>
                        </svg>
                      </button>
                    </template>
                  </template>
                  <button @click.stop="removeHotOrder(slotProps.data)" v-if="userStore.hasPermission('BIG_BOSS')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-trash2-icon lucide-trash-2">
                      <path d="M10 11v6"/>
                      <path d="M14 11v6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                      <path d="M3 6h18"/>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                  <button @click.stop="showHotOrderLog(slotProps.data)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-history-icon lucide-history">
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                      <path d="M3 3v5h5"/>
                      <path d="M12 7v5l4 2"/>
                    </svg>
                  </button>
                </div>
              </template>
            </Column>
            <template #empty>
              <h1 class="py-2 text-center text-lg">No Data</h1>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
    <UpdateHotOrder v-if="isShowUpdateHotOrderModal" :order="order" :orderType="orderType"
                    :selectedHotOrder="selectedHotOrder" @refresh="getHotOrders"
                    @refreshOrder="() => {emit('refreshOrder')}"/>
    <AddHotOrder v-if="isShowAddHotOrderModal" :order="order" :orderType="orderType" :addOrder="addOrder"
                 @refresh="getHotOrders" @refreshOrder="() => {emit('refreshOrder')}"/>
    <HotOrderLog v-if="isShowHotOrderLog" @closeModal="closeHotOrderLog" :hotOrder="selectedHotOrder"/>
    <DeliveredRating v-if="isShowDeliveredRatingModal" :orderType="props.orderType" :hotorder="selectedHotOrder"
                     :onlyRating="true"
                     @refreshHotOrders="getHotOrders"/>
    <CanceledRating v-if="isShowCanceledRatingModal" :orderType="props.orderType" :hotorder="selectedHotOrder"
                    :onlyRating="true"
                    @refreshHotOrders="getHotOrders"/>
    <AddUpdateDog v-if="isShowAddUpdateDogModal" :hotorder="selectedHotOrder" @refreshHotOrders="getHotOrders"/>
    <UpdateUpdateDog v-if="isShowUpdateUpdateDogModal" :hotorder="selectedHotOrder" @refreshHotOrders="getHotOrders"/>
  </div>
</template>

<style scoped>
:deep(.p-datatable-empty-message) {
  background: transparent;
}
</style>
